import type { ReactNode } from 'react'

import {
  AccountLaneHeader,
  type LaneActions,
} from '@/modules/projects/components/Workbench/AccountLaneHeader'
import { LaneFrame } from '@/modules/projects/components/Workbench/LaneFrame'
import type { MapLane } from '@/modules/projects/components/Workbench/map'
import { blockYears } from '@/modules/projects/components/Workbench/mapBlocks'
import { BlockHeading } from './BlockHeading'
import { BlockYearRows, type BlockYearRowsProps } from './BlockYearRows'

interface AccountBlockProps extends Omit<BlockYearRowsProps, 'row'> {
  lanes: MapLane[]
  laneActions: LaneActions
  children?: ReactNode
}

// Years sit further apart than two account rows, so each year still reads as one group.
const BLOCK_YEAR_GAP = 'space-y-6'

// Accounts in a chain of shared statements can still each have statements of their own.
const fileTheSame = (lanes: MapLane[]) =>
  lanes.every(
    (lane) =>
      lane.statementCount === lanes[0]?.statementCount &&
      lane.pageCount === lanes[0]?.pageCount,
  )

// Accounts the bank mails on the same statements, drawn as one block with a row per account.
// A rule between accounts keeps each one's facts together, since they share the same labels.
export const AccountBlock = ({
  lanes,
  laneActions,
  children,
  ...shared
}: AccountBlockProps) => {
  const years = blockYears(lanes)
  const filedOnce = fileTheSame(lanes)
  return (
    <LaneFrame
      label={
        <>
          <BlockHeading filing={filedOnce ? (lanes[0] ?? null) : null} />
          <div className="divide-y border-t">
            {lanes.map((lane) => (
              <div key={lane.accountId} className="py-4 last:pb-0">
                <AccountLaneHeader
                  lane={lane}
                  laneActions={laneActions}
                  filingShown={!filedOnce}
                />
              </div>
            ))}
          </div>
        </>
      }
      timeline={
        <div className={BLOCK_YEAR_GAP}>
          {years.map((row) => (
            <BlockYearRows key={row.year} row={row} {...shared} />
          ))}
        </div>
      }
      labelStays={years.length > 1}
    >
      {children}
    </LaneFrame>
  )
}
